import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface VendorAddProductsScreenProps {
  vendorId: string;
}

/** Mirrors AddProductsToCatalogueScreen.tsx exactly, swapped to the
 * vendor<->product linking endpoints (excludeVendorId/addProductToVendor)
 * and with the inline "create new product, pre-linked" option enabled. */
export const VendorAddProductsScreen: React.FC<VendorAddProductsScreenProps> = ({ vendorId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [showSelector, setShowSelector] = useState(true);
  const [adding, setAdding] = useState(false);

  const handleConfirm = async (selectedProductIds: string[]) => {
    if (selectedProductIds.length === 0) {
      showNotification('warning', 'No Selection', 'Please select at least one product');
      return;
    }

    try {
      setAdding(true);
      let successCount = 0;
      let failCount = 0;

      for (const productId of selectedProductIds) {
        try {
          const response = await apiService.addProductToVendor(vendorId, productId);
          if (response.statusCode === 1000) successCount++;
          else failCount++;
        } catch {
          failCount++;
        }
      }

      if (successCount > 0) {
        showNotification(
          'success',
          'Success',
          `Successfully linked ${successCount} ${successCount === 1 ? 'product' : 'products'} to this vendor.${failCount > 0 ? ` ${failCount} failed.` : ''}`
        );
        setTimeout(() => navigateTo('vendor-detail', vendorId), 1500);
      } else {
        showNotification('error', 'Error', 'Failed to link products to vendor');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to link products to vendor');
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => navigateTo('vendor-detail', vendorId);
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title="Add Products to Vendor" onBack={handleClose} />}
    >
      <View style={styles.screen}>
        <ProductSelectorModal
          visible={showSelector}
          onClose={handleClose}
          onConfirm={handleConfirm}
          excludeVendorId={vendorId}
          allowCreateForVendorId={vendorId}
          title="Add Products to Vendor"
          confirmText={adding ? 'Adding...' : 'Link Selected'}
          allowMultiple={true}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
});
